import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Paper, Avatar, IconButton,
  Button, Tooltip, Stack, Grid, Fade, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  SmartToy, Send, ContentCopy, RestartAlt, CheckCircle,
  Cancel as CancelIcon, Done, Info, Person
} from '@mui/icons-material';
import { useAuth } from '../../hooks/AuthContext';
import { getApiToken } from '../../services/authConfig';
import { useTenders } from '../../hooks/TenderContext';

const API_BASE = process.env.REACT_APP_API_URL || '';

const QUICK_SUGGESTIONS = [
  { label: 'מכרזים פעילים', query: 'הראה לי את כל המכרזים הפעילים', description: 'רשימת כל המכרזים שפתוחים להגשה כרגע' },
  { label: 'מועדי הגשה קרובים', query: 'אילו מכרזים מגיעים למועד הגשה בשבוע הקרוב?', description: 'מכרזים דחופים לימים הקרובים' },
  { label: 'סיכום שבועי', query: 'תן לי סיכום שבועי של המכרזים', description: 'מה קרה השבוע בתחום המכרזים' },
  { label: 'אנשי מפתח', query: 'הראה לי את רשימת אנשי המפתח במערכת', description: 'אנשי קשר ובעלי תפקידים' },
  { label: 'פרויקטים', query: 'מה הפרויקטים שקיימים במאגר?', description: 'סקירת פרויקטים מקושרים' },
  { label: 'סטטיסטיקות', query: 'תן לי סטטיסטיקות על המכרזים', description: 'נתונים מספריים וגרפים' },
];

// --- Helper Functions ---

const renderInlineText = (text) => {
  if (typeof text !== 'string') return text;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
};

const formatMessage = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let tableRows = [];
  let inTable = false;

  const flushTable = (key) => {
    if (tableRows.length > 0) {
      elements.push(
        <Box key={key} sx={{ overflowX: 'auto', my: 2, borderRadius: 2, border: '1px solid #E3E8EF' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.9rem' }}>
            <thead>
              <tr>
                {tableRows[0].map((h, hi) => (
                  <th key={hi} style={{
                    padding: '12px 16px', background: '#F8FAFC', borderBottom: '2px solid #E3E8EF',
                    textAlign: 'right', fontWeight: 600, color: '#475569'
                  }}>{renderInlineText(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.slice(1).map((row, ri) => (
                <tr key={ri} style={{ background: '#fff', borderBottom: '1px solid #F1F5F9' }}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{
                      padding: '10px 16px', textAlign: 'right', color: '#1E293B'
                    }}>{renderInlineText(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      );
      tableRows = [];
    }
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();

    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const cells = trimmed.split('|').filter(c => c.trim()).map(c => c.trim());
      if (cells.every(c => /^[-:]+$/.test(c))) return;
      tableRows.push(cells);
      inTable = true;
      return;
    }

    if (inTable) {
      flushTable(`table-${i}`);
      inTable = false;
    }

    if (/^\d+[\.\)]/.test(trimmed)) {
      elements.push(
        <Typography key={i} variant="body1" sx={{ py: 0.5, lineHeight: 1.8, color: '#334155' }}>
          {renderInlineText(trimmed)}
        </Typography>
      );
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      elements.push(
        <Typography key={i} variant="body1" sx={{ py: 0.5, lineHeight: 1.8, pr: 2, position: 'relative', color: '#334155' }}>
          <span style={{ position: 'absolute', right: 0, color: '#94A3B8' }}>•</span> {renderInlineText(trimmed.substring(2))}
        </Typography>
      );
    } else if (trimmed.startsWith('##')) {
      elements.push(
        <Typography key={i} variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1, color: '#0F172A', textAlign: 'right' }}>
          {trimmed.replace(/^#+\s*/, '')}
        </Typography>
      );
    } else if (trimmed === '') {
      elements.push(<Box key={i} sx={{ height: 12 }} />);
    } else {
      elements.push(
        <Typography key={i} variant="body1" sx={{ py: 0.5, lineHeight: 1.8, color: '#334155' }}>
          {renderInlineText(trimmed)}
        </Typography>
      );
    }
  });

  if (inTable) flushTable('table-end');
  return elements;
};

const ModernTypingIndicator = () => (
  <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center', minHeight: 24, pt: 1 }}>
    {[0, 1, 2].map(d => (
      <Box key={d} sx={{
        width: 8, height: 8, borderRadius: '50%', bgcolor: '#94A3B8',
        animation: 'pulse 1.4s infinite ease-in-out',
        animationDelay: `${d * 0.2}s`,
        '@keyframes pulse': {
          '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
          '40%': { opacity: 1, transform: 'scale(1)' },
        },
      }} />
    ))}
  </Box>
);

// --- Main Component ---

const AgentPage = () => {
  const { currentUser } = useAuth();
  const { stats } = useTenders();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, pendingAction, scrollToBottom]);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  const getSmartWelcome = useCallback(() => {
    const parts = [];
    parts.push(`שלום **${currentUser?.name || ''}**! 👋`);
    parts.push('אני העוזר החכם של מערכת ניהול המכרזים WXG.');
    const total = stats?.total || 0;
    const upcoming = stats?.upcoming || 0;
    if (total > 0) parts.push(`כרגע מעודכנים במערכת **${total}** מכרזים.`);
    if (upcoming > 0) parts.push(`**${upcoming}** מהם מגיעים למועד הגשה קרוב.`);
    parts.push('איך אוכל לעזור לך היום?');
    return parts.join(' ');
  }, [currentUser, stats]);

  useEffect(() => {
    setMessages([{ role: 'assistant', content: getSmartWelcome() }]);
  }, [getSmartWelcome]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);

    try {
      const token = await getApiToken();
      const history = messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content }));

      const res = await fetch(`${API_BASE}/agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        // Base64 encoding for safe transport of Hebrew/Unicode text through WAF
        body: JSON.stringify({ payload: btoa(unescape(encodeURIComponent(JSON.stringify({ message: msg, history })))) }),
      });

      const data = await res.json();
      if (data.error) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.error, isError: true }]);
        return;
      }

      const cleanReply = (data.reply || '').replace(/<action>[\s\S]*?<\/action>/g, '').trim();
      if (data.action) setPendingAction(data.action);

      setMessages(prev => [...prev, { role: 'assistant', content: cleanReply, hasAction: !!data.action }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'מצטער, אירעה שגיאה בתקשורת. נסה שוב מאוחר יותר.', isError: true }]);
    } finally {
      setLoading(false);
    }
  };

  const executeAction = async (confirmed) => {
    if (!confirmed || !pendingAction) {
      setPendingAction(null);
      setMessages(prev => [...prev, { role: 'assistant', content: 'הפעולה בוטלה על ידי המשתמש.' }]);
      return;
    }
    setLoading(true);
    try {
      const token = await getApiToken();
      const res = await fetch(`${API_BASE}/agent/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: pendingAction }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.success ? `${data.message}` : `${data.error || 'ביצוע הפעולה נכשל.'}`,
        isError: !data.success,
      }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'שגיאה חמורה בעת ביצוע הפעולה.', isError: true }]);
    } finally {
      setPendingAction(null);
      setLoading(false);
    }
  };

  const resetChat = () => {
    setMessages([{ role: 'assistant', content: getSmartWelcome() }]);
    setPendingAction(null);
  };

  const copyText = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const actionLabels = {
    create_tender: 'יצירת מכרז חדש',
    update_tender: 'עדכון מכרז קיים',
    delete_tender: 'מחיקת מכרז מהמערכת',
  };

  const isInitialState = messages.length <= 1 && !loading;

  return (
    <Box dir="rtl" sx={{
      display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)', // adjust for your navbar
      bgcolor: '#FFFFFF', color: '#0F172A', position: 'relative'
    }}>
      
      {/* Minimal Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        p: 2, px: 3, borderBottom: '1px solid #F1F5F9'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#1E293B' }}>WXG AI</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="שיחה חדשה">
            <IconButton onClick={resetChat} sx={{ color: '#64748B' }}><RestartAlt /></IconButton>
          </Tooltip>
          <Tooltip title="מידע על המערכת">
            <IconButton onClick={() => setInfoOpen(true)} sx={{ color: '#64748B' }}><Info /></IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Info Dialog */}
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} dir="rtl">
        <DialogTitle sx={{ fontWeight: 700 }}>אודות העוזר החכם</DialogTitle>
        <DialogContent sx={{ minWidth: { xs: 'auto', sm: 400 } }}>
          <Typography variant="body1" paragraph>
            העוזר החכם של מערכת <strong>WXG Tenders</strong> מאפשר לך לשלוף נתונים ולבצע פעולות בשפה חופשית.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setInfoOpen(false)} variant="contained" sx={{ bgcolor: '#0F172A' }}>סגור</Button>
        </DialogActions>
      </Dialog>

      {/* Main Stream Area */}
      <Box sx={{ flex: 1, overflowY: 'auto', pb: 15, pt: 4 }}>
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
          
          {messages.map((msg, i) => {
            const isUser = msg.role === 'user';
            const isError = msg.isError;

            return (
              <Fade in={true} key={i}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  
                  {/* Avatar */}
                  <Avatar sx={{
                    width: 36, height: 36, flexShrink: 0,
                    bgcolor: isUser ? '#F1F5F9' : (isError ? '#FEF2F2' : '#EFF6FF'),
                    color: isUser ? '#64748B' : (isError ? '#EF4444' : '#2563EB'),
                  }}>
                    {isUser ? <Person fontSize="small" /> : <SmartToy fontSize="small" />}
                  </Avatar>

                  {/* Message Content */}
                  <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0F172A', mb: 1 }}>
                      {isUser ? (currentUser?.name || 'את/ה') : 'WXG AI'}
                    </Typography>
                    
                    <Box sx={{ color: isError ? '#EF4444' : '#334155' }}>
                      {isUser ? (
                        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>{msg.content}</Typography>
                      ) : (
                        formatMessage(msg.content)
                      )}
                    </Box>

                    {/* Gemini Style Action Bar (Under the text, aligned to the left!) */}
                    {!isUser && !isError && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Tooltip title={copiedIndex === i ? 'הועתק!' : 'העתק'}>
                          <IconButton
                            size="small"
                            onClick={() => copyText(msg.content, i)}
                            sx={{ color: copiedIndex === i ? '#10B981' : '#94A3B8', '&:hover': { bgcolor: '#F1F5F9' } }}
                          >
                            {copiedIndex === i ? <Done sx={{ fontSize: 18 }} /> : <ContentCopy sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Fade>
            );
          })}

          {/* Pending Action Box */}
          {pendingAction && (
            <Fade in={true}>
              <Paper elevation={0} sx={{
                p: 3, ml: 6, borderRadius: 2, bgcolor: '#FFFBEB',
                border: '1px solid #FEF3C7', borderRight: '4px solid #F59E0B'
              }}>
                <Typography variant="subtitle1" fontWeight={700} color="#B45309" sx={{ mb: 1 }}>
                  נדרש אישור פעולה: {actionLabels[pendingAction.type] || pendingAction.type}
                </Typography>
                {pendingAction.summary && (
                  <Typography variant="body2" sx={{ mb: 2, color: '#92400E' }}>
                    {pendingAction.summary}
                  </Typography>
                )}
                <Stack direction="row" spacing={2}>
                  <Button variant="contained" color="success" onClick={() => executeAction(true)} disabled={loading} disableElevation>
                    אישור
                  </Button>
                  <Button variant="outlined" color="inherit" onClick={() => executeAction(false)} disabled={loading}>
                    ביטול
                  </Button>
                </Stack>
              </Paper>
            </Fade>
          )}

          {/* Loading Indicator */}
          {loading && (
            <Fade in={true}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: '#EFF6FF', color: '#2563EB' }}>
                  <SmartToy fontSize="small" />
                </Avatar>
                <Box sx={{ flex: 1, pt: 1 }}>
                  <ModernTypingIndicator />
                </Box>
              </Box>
            </Fade>
          )}

          <div ref={messagesEndRef} />
        </Box>
      </Box>

      {/* Initial State / Quick Suggestions */}
      {isInitialState && (
        <Box sx={{
          position: 'absolute', top: '45%', left: '50%', transform: 'translate(-50%, -50%)',
          width: '100%', maxWidth: 800, px: 2, textAlign: 'center', zIndex: 1
        }}>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 4, color: '#0F172A' }}>
            במה אוכל לעזור היום?
          </Typography>
          <Grid container spacing={2} dir="rtl">
            {QUICK_SUGGESTIONS.map((s, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Paper
                  elevation={0}
                  onClick={() => sendMessage(s.query)}
                  sx={{
                    p: 2.5, borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#F8FAFC',
                    cursor: 'pointer', height: '100%',
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', // Forces elements to start from physical right in RTL
                    textAlign: 'right', // Backup alignment
                    transition: 'background-color 0.2s',
                    '&:hover': { bgcolor: '#F1F5F9' }
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, color: '#0F172A' }}>
                    {s.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.5 }}>
                    {s.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Gemini Style Input Area */}
      <Box sx={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        pb: 3, pt: 2, px: 2,
        background: 'linear-gradient(to top, #ffffff 60%, rgba(255,255,255,0))',
        zIndex: 5
      }}>
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Paper elevation={0} sx={{
            display: 'flex', alignItems: 'flex-end', gap: 1,
            p: 1, pl: 1, pr: 2, borderRadius: 8,
            border: '1px solid #E2E8F0', bgcolor: '#F8FAFC',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
            '&:focus-within': { borderColor: '#CBD5E1', bgcolor: '#fff' }
          }}>
            <TextField
              inputRef={inputRef}
              fullWidth
              placeholder="שאל אותי משהו..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              multiline
              maxRows={6}
              disabled={loading}
              InputProps={{ dir: 'rtl' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '1rem', py: 1.5,
                  '& fieldset': { border: 'none' },
                },
              }}
            />
            <IconButton
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              sx={{
                bgcolor: input.trim() ? '#0F172A' : '#E2E8F0', 
                color: input.trim() ? '#fff' : '#94A3B8',
                width: 44, height: 44, flexShrink: 0, mb: 0.5,
                '&:hover': { bgcolor: input.trim() ? '#334155' : '#E2E8F0' },
              }}
            >
              <Send sx={{ fontSize: 20, transform: 'scaleX(-1)' }} />
            </IconButton>
          </Paper>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1.5, color: '#94A3B8' }}>
            AI עשוי להציג מידע לא מדויק. מומלץ לוודא נתונים חשובים.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AgentPage;